import { z } from 'zod';
import {
  CONTRACT_MODELS,
  CONTRACT_STATUSES,
  INVOICE_STATUSES,
  LABEL_COLORS,
  PAYMENT_METHODS,
  RECURRENCE_PERIODS,
} from '../domain';
import { patchOf } from './patch';

const cents = z.number().int().min(0).max(1e13);
const day = z.iso.date();
const optText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => v || null);

// Contratos (SPEC §32) ---------------------------------------------------------------

export const contractInput = z.object({
  clientId: z.uuid(),
  title: z.string().trim().min(1, 'Dê um nome ao contrato').max(160),
  model: z.enum(CONTRACT_MODELS),
  status: z.enum(CONTRACT_STATUSES).default('active'),
  startsOn: day,
  endsOn: day
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  amountCents: cents.default(0),
  recurrence: z.enum(RECURRENCE_PERIODS).default('monthly'),
  billingDay: z.number().int().min(1).max(28).default(5),
  rules: optText(4000),
  notes: optText(4000),
  services: z
    .array(
      z.object({
        serviceTypeId: z.uuid(),
        priceCents: cents
          .nullable()
          .optional()
          .transform((v) => v ?? null),
        includedQuantity: z.number().int().min(0).max(100_000).default(0),
      }),
    )
    .max(200)
    .default([]),
});
export type ContractInput = z.input<typeof contractInput>;
export const contractPatch = patchOf(contractInput.omit({ clientId: true }));

export const contractDto = z.object({
  id: z.uuid(),
  client: z.object({ id: z.uuid(), name: z.string(), color: z.enum(LABEL_COLORS) }),
  title: z.string(),
  model: z.enum(CONTRACT_MODELS),
  status: z.enum(CONTRACT_STATUSES),
  startsOn: z.string(),
  endsOn: z.string().nullable(),
  amountCents: z.number(),
  recurrence: z.enum(RECURRENCE_PERIODS),
  billingDay: z.number(),
  rules: z.string().nullable(),
  notes: z.string().nullable(),
  services: z.array(
    z.object({
      serviceTypeId: z.uuid(),
      name: z.string(),
      basePriceCents: z.number().nullable(),
      priceCents: z.number().nullable(),
      includedQuantity: z.number(),
    }),
  ),
  /** Receita esperada por mês (fixo/híbrido) para a previsão. */
  monthlyCents: z.number(),
  createdAt: z.string(),
});
export type ContractDto = z.infer<typeof contractDto>;

// Faturas (SPEC §33) -----------------------------------------------------------------

const invoiceItemInput = z.object({
  serviceTypeId: z
    .uuid()
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  taskItemId: z
    .uuid()
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  description: z.string().trim().min(1).max(300),
  quantity: z.number().int().min(1).max(100_000).default(1),
  unitPriceCents: cents,
});

export const invoiceInput = z.object({
  clientId: z.uuid(),
  contractId: z
    .uuid()
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  issueDate: day,
  dueDate: day,
  periodStart: day
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  periodEnd: day
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  discountCents: cents.default(0),
  notes: optText(4000),
  items: z.array(invoiceItemInput).min(1, 'Inclua ao menos um item').max(500),
});
export type InvoiceInput = z.input<typeof invoiceInput>;
export const invoicePatch = patchOf(invoiceInput.omit({ clientId: true }));

/** Monta a fatura de um contrato pelo período (fixo, volume, híbrido). */
export const generateInvoiceInput = z.object({
  contractId: z.uuid(),
  periodStart: day,
  periodEnd: day,
  dueDate: day.optional(),
});

export const invoiceItemDto = z.object({
  id: z.uuid(),
  serviceTypeId: z.uuid().nullable(),
  taskItemId: z.uuid().nullable(),
  description: z.string(),
  quantity: z.number(),
  unitPriceCents: z.number(),
  totalCents: z.number(),
});

export const paymentDto = z.object({
  id: z.uuid(),
  amountCents: z.number(),
  paidOn: z.string(),
  method: z.enum(PAYMENT_METHODS),
  notes: z.string().nullable(),
  createdBy: z.string().nullable(),
});

export const invoiceDto = z.object({
  id: z.uuid(),
  number: z.number(),
  client: z.object({ id: z.uuid(), name: z.string(), color: z.enum(LABEL_COLORS) }),
  contract: z.object({ id: z.uuid(), title: z.string() }).nullable(),
  /** Status efetivo: considera pagamentos e vencimento. */
  status: z.enum(INVOICE_STATUSES),
  issueDate: z.string(),
  dueDate: z.string(),
  periodStart: z.string().nullable(),
  periodEnd: z.string().nullable(),
  subtotalCents: z.number(),
  discountCents: z.number(),
  totalCents: z.number(),
  paidCents: z.number(),
  notes: z.string().nullable(),
  items: z.array(invoiceItemDto),
  payments: z.array(paymentDto),
  createdAt: z.string(),
});
export type InvoiceDto = z.infer<typeof invoiceDto>;

export const paymentInput = z.object({
  amountCents: cents.min(1, 'Informe o valor'),
  paidOn: day,
  method: z.enum(PAYMENT_METHODS).default('pix'),
  notes: optText(1000),
});

// Despesas (SPEC §34) ----------------------------------------------------------------

export const expenseInput = z.object({
  description: z.string().trim().min(1, 'Descreva a despesa').max(300),
  amountCents: cents.min(1, 'Informe o valor'),
  spentOn: day,
  categoryId: z
    .uuid()
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  clientId: z
    .uuid()
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  projectId: z
    .uuid()
    .nullable()
    .optional()
    .transform((v) => v ?? null),
  vendor: optText(160),
  recurring: z.boolean().default(false),
});
export type ExpenseInput = z.input<typeof expenseInput>;
export const expensePatch = patchOf(expenseInput);

export const expenseDto = z.object({
  id: z.uuid(),
  description: z.string(),
  amountCents: z.number(),
  spentOn: z.string(),
  category: z.object({ id: z.uuid(), name: z.string(), color: z.string() }).nullable(),
  client: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  project: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  vendor: z.string().nullable(),
  recurring: z.boolean(),
});
export type ExpenseDto = z.infer<typeof expenseDto>;

export const expenseCategoryDto = z.object({ id: z.uuid(), name: z.string(), color: z.string() });

// Visão geral -------------------------------------------------------------------------

export const financeOverviewDto = z.object({
  month: z.string(),
  /** Receita recorrente esperada por mês (contratos ativos fixos/híbridos). */
  forecastCents: z.number(),
  invoicedCents: z.number(),
  receivedCents: z.number(),
  pendingCents: z.number(),
  overdueCents: z.number(),
  expensesCents: z.number(),
  marginCents: z.number(),
  /** Últimos 6 meses: faturado, recebido e despesas. */
  series: z.array(
    z.object({
      month: z.string(),
      invoicedCents: z.number(),
      receivedCents: z.number(),
      expensesCents: z.number(),
    }),
  ),
});
export type FinanceOverviewDto = z.infer<typeof financeOverviewDto>;

export const clientFinanceSummaryDto = z.object({
  from: z.string(),
  to: z.string(),
  timezone: z.string(),
  contracts: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      model: z.enum(CONTRACT_MODELS),
      amountCents: z.number(),
      recurrence: z.enum(RECURRENCE_PERIODS),
      startsOn: z.string(),
      endsOn: z.string().nullable(),
    }),
  ),
  items: z.array(
    z.object({
      id: z.uuid(),
      taskId: z.uuid(),
      title: z.string(),
      project: z.string().nullable(),
      service: z.string(),
      quantity: z.number(),
      unitPriceCents: z.number().nullable(),
      totalCents: z.number().nullable(),
      contractId: z.uuid().nullable(),
      model: z.enum(CONTRACT_MODELS).nullable(),
      completed: z.boolean(),
      date: z.string(),
      billed: z.boolean(),
      pending: z.enum(['contract', 'price']).nullable(),
    }),
  ),
  productionCents: z.number(),
  accumulatedCents: z.number(),
  toInvoiceCents: z.number(),
  invoicedCents: z.number(),
  paidCents: z.number(),
  openCents: z.number(),
  pendingCount: z.number(),
});
export type ClientFinanceSummaryDto = z.infer<typeof clientFinanceSummaryDto>;
